// @flow

/**
 * Module dependencies.
 */

import { FormGroup, Label, makeFormControl } from 'client/components/core/forms/styles';
import { colors, media, units } from 'client/styles';
import { compose, defaultProps } from 'recompose';
import { formatAmount, getNumbers } from 'client/core/utils';
import { ifProp } from 'styled-tools';
import { isFunction, isNil } from 'lodash';
import { position } from 'polished';
import Icon from 'client/components/core/icon';
import RawHtml from 'react-raw-html';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  label: string,
  maxLenght: number,
  minimumValue?: string,
  onUpdate?: Function,
  placeholder?: string,
  prefix: string,
  thousandSeparator: string,
  tooltip?: string,
  tooltipSeparator: string,
  validation?: Function,
  value: string
};

/**
 * `InputFormControl` component.
 */

const InputFormControl = makeFormControl();

/**
 * `Tooltip` component.
 */

const Tooltip = styled.div`
  align-items: center;
  border-radius: 0 0 4px 4px;
  display: flex;
  height: ${ifProp('visible', '24px', 0)};
  overflow: hidden;
  padding-left: ${units(0.5)};
  position: relative;
  text-align: left;
  transition: height 0.5s;
`;

/**
 * `TooltipBackground` component.
 */

const TooltipBackground = styled.div`
  background-color: ${colors.shadesOfBlue.iron};
  opacity: 0.4;
  z-index: -1;

  ${position('absolute', '0', '0', '0', '0')}
`;

/**
 * Tooltip icon style.
 */

const tooltipIconStyle = {
  position: 'relative',
  top: '1px'
};

/**
 * `TooltipSeparator` component.
 */

// $FlowFixMe
const TooltipSeparator = styled(Text.Small)`
  color: ${colors.shadesOfBlue.regentGray};
  margin: 0;

  ${media.portrait.xxs`
    display: none;
  `}
`;

/**
 * `TooltipText` component.
 */

// $FlowFixMe
const TooltipText = styled(Text.Small)`
  color: ${colors.brandSecondary};
  margin: 0;
  opacity: 1;
  padding-left: ${units(1)};

  ${media.portrait.xxs`
    padding-left: 1px;
  `}
`;

/**
 * `InputComponent` component.
 */

class InputComponent extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * State.
   */

  state = {
    hasError: false,
    hasFocus: false,
    hasValue: false,
    isShowingTooltip: false,
    value: this.props.value || 0
  };

  /**
   * Input.
   */

  input: ?Object;

  /**
   * Set `input` ref.
   */

  setInput = (input: ?Object) => {
    this.input = input;
  };

  /**
   * On key down.
   */

  onKeyDown = (event: any) => {
    const charCode = event.which ? event.which : event.keyCode;
    const charCodeNavigation = charCode < 37 || charCode > 40;
    const charCodeDelete = charCode === 8;

    const strippedValue = getNumbers(this.state.value);
    const maxLenght = strippedValue.length < this.props.maxLenght;

    // Max lenght validation.
    if (!maxLenght && !charCodeDelete && charCodeNavigation) {
      event.preventDefault();
    }
  }

  /**
   * On change.
   */

  onChange = (event: any) => {
    const { minimumValue, onUpdate, validation } = this.props;
    const { value } = event.target;
    const { prefix, thousandSeparator } = this.props;
    const amount = getNumbers(value);
    const hasError = isFunction(validation) && minimumValue ? !validation(amount, minimumValue) : !value;
    const eventOnChange = { target: event.target };
    const callback = () => {
      return isFunction(onUpdate) ? onUpdate(eventOnChange) : null;
    };

    this.setState({
      hasError,
      hasValue: !isNil(value),
      isShowingTooltip: true,
      value: formatAmount(value, prefix, thousandSeparator)
    }, callback);
  }

  /**
   * On blur.
   */

  onBlur = (event: any) => {
    this.setState({
      hasFocus: false,
      hasValue: !isNil(event.currentTarget.value),
      isShowingTooltip: this.state.hasError
    });
  }

  /**
   * On focus.
   */

  onFocus = (event: any) => {
    this.setState({
      hasFocus: true,
      hasValue: !isNil(event.currentTarget.value)
    });
  }

  /**
   * Component will receive props.
   */

  componentWillReceiveProps({ prefix, thousandSeparator, value }: Props) {
    let currentValue = this.state.value;

    if (value !== this.state.value) {
      currentValue = value;
    }

    this.setState({
      hasValue: !isNil(currentValue),
      value: formatAmount(currentValue, prefix, thousandSeparator)
    });
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const { hasError, hasFocus, hasValue, isShowingTooltip, value } = this.state;
    const { label, tooltip, tooltipSeparator, ...rest } = this.props;

    return (
      <div>
        <FormGroup
          hasError={hasError}
          hasFocus={hasFocus}
        >
          <Label
            active={hasFocus || hasValue}
            hasError={hasError}
            hasFocus={hasFocus}
          >
            {label}
          </Label>

          <InputFormControl
            {...rest}
            autoComplete={false}
            innerRef={this.setInput}
            onBlur={this.onBlur}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onKeyDown={this.onKeyDown}
            value={value}
          />
        </FormGroup>

        <Tooltip visible={isShowingTooltip}>
          <TooltipBackground />

          <Icon
            icon={'info'}
            size={'16px'}
            styles={tooltipIconStyle}
          />

          <TooltipSeparator>
            {tooltipSeparator}
          </TooltipSeparator>

          <TooltipText>
            <RawHtml.span>
              {tooltip}
            </RawHtml.span>
          </TooltipText>
        </Tooltip>
      </div>
    );
  }

}

/**
 * Export `Amount`.
 */

export const Amount = compose(
  defaultProps({
    maxLenght: Infinity,
    tooltipSeparator: '|'
  })
)(InputComponent);



// WEBPACK FOOTER //
// ../src/client/components/core/forms/amount.js